import { supabase } from '../lib/supabase.js';
import { buildSnapshot, encodeSnapshot, decodeSnapshot, isEmptySnapshot } from './snapshot.js';
import { getMeta, setMeta } from '../db/meta.js';
import { dayKey } from '../scheduler/day.js';

const BUCKET = 'backups';
/** Automatic copies only. Manual backups stay. Seven days fits the free storage quota. */
export const KEEP_CLOUD = 7;

/**
 * Upload a compressed snapshot to Supabase Storage. Independent of row sync:
 * even if replication state were ever damaged, these files restore everything.
 */
export async function uploadCloudSnapshot(db, userId, reason = 'auto') {
  if (!supabase) throw new Error('Cloud sync isn’t set up.');
  const snap = await buildSnapshot(db, { reason });
  if (isEmptySnapshot(snap)) return null;
  const bytes = encodeSnapshot(snap);
  const stamp = new Date(snap.createdAt).toISOString().replace(/[:.]/g, '-');
  const path = `${userId}/${stamp}-${snap.deviceId}.json.gz`;
  const { error: upErr } = await supabase.storage
    .from(BUCKET)
    .upload(path, new Blob([bytes], { type: 'application/gzip' }), {
      contentType: 'application/gzip',
      upsert: false,
      cacheControl: '0',
    });
  if (upErr) throw new Error(`Cloud backup upload failed: ${upErr.message}`);
  const { error: rowErr } = await supabase.from('backups').insert({
    storage_path: path,
    device_id: snap.deviceId,
    reason,
    counts: snap.counts,
    size_bytes: bytes.length,
  });
  if (rowErr) {
    await supabase.storage.from(BUCKET).remove([path]);
    throw new Error(`Cloud backup record failed: ${rowErr.message}`);
  }
  await pruneCloud().catch(() => {});
  return path;
}

export async function listCloudSnapshots() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('backups')
    .select('id, storage_path, device_id, reason, counts, size_bytes, created_at')
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function downloadCloudSnapshot(path) {
  if (!supabase) throw new Error('Cloud sync isn’t set up.');
  const { data, error } = await supabase.storage.from(BUCKET).download(path);
  if (error) throw new Error(`Download failed: ${error.message}`);
  return decodeSnapshot(new Uint8Array(await data.arrayBuffer()));
}

export async function deleteCloudSnapshot(row) {
  if (!supabase) throw new Error('Cloud sync isn’t set up.');
  await supabase.storage.from(BUCKET).remove([row.storage_path]);
  const { error } = await supabase.from('backups').delete().eq('id', row.id);
  if (error) throw new Error(error.message);
}

export async function pruneCloud(keep = KEEP_CLOUD) {
  const rows = await listCloudSnapshots();
  const auto = rows.filter((r) => r.reason === 'auto');
  for (const r of auto.slice(keep)) await deleteCloudSnapshot(r);
}

/** At most one automatic cloud snapshot per day per device. */
export async function maybeDailyCloudSnapshot(db, userId) {
  const today = dayKey();
  if ((await getMeta(db, 'lastCloudBackupDay')) === today) return null;
  const path = await uploadCloudSnapshot(db, userId, 'auto');
  await setMeta(db, 'lastCloudBackupDay', today);
  await setMeta(db, 'lastCloudBackupAt', Date.now());
  return path;
}
